import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    ArrowLeft, Building2, ChevronRight, Layers, CheckCircle,
    Clock, AlertCircle, TrendingUp, Eye, Edit3, Search
} from 'lucide-react';
import { Button, CardStats, Card } from '@components/ui';
import { getConstructionById } from '@mocks/insaatlar';
import { getBlocksByConstructionId } from '@mocks/bloklar';
import {
    getBlokDaireleri, getBlokIlerlemOzeti,
    getDaireIlerleme, durumlar, anaKalemSablonlari
} from '@mocks/daireIlerleme';
import HizliGuncellemeModal from './HizliGuncellemeModal';
import clsx from 'clsx';

export default function BlokDetay() {
    const { id, blokId } = useParams();
    const navigate = useNavigate();
    const [construction, setConstruction] = useState(null);
    const [blok, setBlok] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterDurum, setFilterDurum] = useState('');
    const [quickEditDaire, setQuickEditDaire] = useState(null);

    useEffect(() => {
        const timer = setTimeout(() => {
            const c = getConstructionById(id);
            if (c) {
                setConstruction(c);
                const bloklar = getBlocksByConstructionId(id);
                const b = bloklar.find(bl => bl.id === blokId);
                setBlok(b || null);
            }
            setIsLoading(false);
        }, 400);
        return () => clearTimeout(timer);
    }, [id, blokId]);

    const blokDaireleri = useMemo(() => getBlokDaireleri(blokId), [blokId]);
    const blokOzet = useMemo(() => getBlokIlerlemOzeti(blokId), [blokId]);

    // Tüm daireleri düz liste olarak al
    const tumDaireler = useMemo(() => {
        if (!blokDaireleri) return [];
        return blokDaireleri.katlar.flatMap(kat =>
            kat.daireler.map(d => {
                const ilerleme = getDaireIlerleme(d.id);
                // En kritik (en düşük ilerlemeli devam eden) kalemi bul
                let kritikKalem = null;
                if (ilerleme) {
                    const devamEdenler = ilerleme.kalemler
                        .filter(k => k.durum === 'devam_ediyor')
                        .sort((a, b) => a.yuzde - b.yuzde);
                    if (devamEdenler.length > 0) {
                        const kalemBilgi = anaKalemSablonlari.find(k => k.id === devamEdenler[0].anaKalemId);
                        kritikKalem = kalemBilgi ? `${kalemBilgi.ikon} ${kalemBilgi.name} (%${devamEdenler[0].yuzde})` : null;
                    }
                }
                return {
                    ...d,
                    genelIlerleme: ilerleme?.genelIlerleme || 0,
                    durum: ilerleme?.durum || 'baslanmadi',
                    kritikKalem,
                };
            })
        );
    }, [blokDaireleri]);

    // Filtreleme
    const filteredDaireler = useMemo(() => {
        let list = tumDaireler;
        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            list = list.filter(d => d.no.toLowerCase().includes(term) || d.tip.toLowerCase().includes(term));
        }
        if (filterDurum) {
            list = list.filter(d => d.durum === filterDurum);
        }
        return list;
    }, [tumDaireler, searchTerm, filterDurum]);

    const getDurumBadge = (durum) => {
        const d = durumlar[durum] || durumlar.baslanmadi;
        return <span className={clsx('px-2.5 py-1 rounded-full text-xs font-medium', d.renk)}>{d.label}</span>;
    };

    const getProgressColor = (yuzde) => {
        if (yuzde >= 75) return 'bg-success';
        if (yuzde >= 40) return 'bg-primary';
        if (yuzde > 0) return 'bg-warning';
        return 'bg-bg-tertiary';
    };

    if (isLoading) {
        return (
            <div className="p-4 md:p-6 space-y-6 animate-pulse">
                <div className="h-8 w-64 bg-bg-tertiary rounded" />
                <div className="h-24 bg-bg-tertiary rounded-xl" />
                <div className="h-96 bg-bg-tertiary rounded-xl" />
            </div>
        );
    }

    if (!construction || !blok) {
        return (
            <div className="p-4 md:p-6 text-center py-16">
                <Building2 size={64} className="mx-auto mb-4 text-text-muted opacity-40" />
                <h2 className="text-xl font-bold text-text-primary mb-2">Blok Bulunamadı</h2>
                <Button variant="primary" onClick={() => navigate('/insaat/projeler')}>Projelere Dön</Button>
            </div>
        );
    }

    return (
        <div className="p-4 md:p-6 space-y-6">
            {/* Breadcrumb */}
            <div className="flex items-center gap-2 text-sm text-text-muted flex-wrap">
                <button onClick={() => navigate('/insaat/projeler')} className="hover:text-primary transition-colors">İnşaatlar</button>
                <ChevronRight size={14} />
                <button onClick={() => navigate(`/insaat/projeler/${id}`)} className="hover:text-primary transition-colors">{construction.name}</button>
                <ChevronRight size={14} />
                <span className="text-text-primary font-medium">{blok.name}</span>
            </div>

            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                        <Layers size={24} className="text-primary" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold text-text-primary">{blok.name}</h1>
                        <p className="text-text-muted text-sm">{blok.totalFloors} Kat • {blok.totalApartments} Daire</p>
                    </div>
                </div>
                {blokOzet && (
                    <div className="flex items-center gap-2">
                        <span className="text-3xl font-bold text-text-primary">%{blokOzet.genelIlerleme}</span>
                        <span className="text-sm text-text-muted">İlerleme</span>
                    </div>
                )}
            </div>

            {/* Stats */}
            {blokOzet && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <CardStats title="Toplam Daire" value={blokOzet.toplamDaire} icon={Building2} color="primary" size="sm" />
                    <CardStats title="Tamamlanan" value={blokOzet.tamamlanan} icon={CheckCircle} color="success" size="sm" />
                    <CardStats title="Devam Eden" value={blokOzet.devamEden} icon={Clock} color="warning" size="sm" />
                    <CardStats title="Başlanmadı" value={blokOzet.baslamamis} icon={AlertCircle} color="tertiary" size="sm" />
                </div>
            )}

            {/* Filtreler */}
            <div className="flex flex-col sm:flex-row gap-3">
                <div className="relative flex-1">
                    <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
                    <input
                        type="text"
                        placeholder="Daire ara (no veya tip)..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-9 pr-4 py-2.5 rounded-lg border border-border-light bg-bg-primary text-text-primary text-sm focus:outline-none focus:border-primary"
                    />
                </div>
                <select
                    value={filterDurum}
                    onChange={(e) => setFilterDurum(e.target.value)}
                    className="px-4 py-2.5 rounded-lg border border-border-light bg-bg-primary text-text-primary text-sm focus:outline-none focus:border-primary"
                >
                    <option value="">Tüm Durumlar</option>
                    {Object.entries(durumlar).map(([key, val]) => (
                        <option key={key} value={key}>{val.label}</option>
                    ))}
                </select>
            </div>

            {/* Daire Tablosu */}
            <Card padding="none" className="overflow-hidden">
                {/* Desktop Tablo */}
                <div className="hidden md:block overflow-x-auto">
                    <table className="w-full">
                        <thead>
                            <tr className="border-b border-border-light bg-bg-tertiary/50">
                                <th className="text-left px-4 py-3 text-xs font-semibold text-text-muted uppercase">Kat</th>
                                <th className="text-left px-4 py-3 text-xs font-semibold text-text-muted uppercase">Daire No</th>
                                <th className="text-left px-4 py-3 text-xs font-semibold text-text-muted uppercase">Tip</th>
                                <th className="text-left px-4 py-3 text-xs font-semibold text-text-muted uppercase">İlerleme</th>
                                <th className="text-left px-4 py-3 text-xs font-semibold text-text-muted uppercase">Kritik Kalem</th>
                                <th className="text-left px-4 py-3 text-xs font-semibold text-text-muted uppercase">Durum</th>
                                <th className="text-right px-4 py-3 text-xs font-semibold text-text-muted uppercase">İşlem</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredDaireler.map((daire, idx) => (
                                <tr
                                    key={daire.id}
                                    className={clsx(
                                        'border-b border-border-light/50 hover:bg-bg-hover cursor-pointer transition-colors',
                                        idx % 2 === 0 ? 'bg-bg-secondary' : 'bg-bg-primary'
                                    )}
                                    onClick={() => navigate(`/insaat/projeler/${id}/blok/${blokId}/daire/${daire.id}`)}
                                >
                                    <td className="px-4 py-3 text-sm text-text-primary font-medium">{daire.kat}. Kat</td>
                                    <td className="px-4 py-3 text-sm text-text-primary font-semibold">{daire.no}</td>
                                    <td className="px-4 py-3 text-sm text-text-secondary">{daire.tip}</td>
                                    <td className="px-4 py-3">
                                        <div className="flex items-center gap-3">
                                            <div className="w-24 h-2 bg-bg-tertiary rounded-full overflow-hidden">
                                                <div className={clsx('h-full rounded-full', getProgressColor(daire.genelIlerleme))} style={{ width: `${daire.genelIlerleme}%` }} />
                                            </div>
                                            <span className="text-sm font-medium text-text-primary w-10">%{daire.genelIlerleme}</span>
                                        </div>
                                    </td>
                                    <td className="px-4 py-3 text-xs text-text-secondary">{daire.kritikKalem || '—'}</td>
                                    <td className="px-4 py-3">{getDurumBadge(daire.durum)}</td>
                                    <td className="px-4 py-3 text-right">
                                        <div className="flex items-center justify-end gap-1">
                                            <button
                                                onClick={(e) => { e.stopPropagation(); setQuickEditDaire(daire); }}
                                                className="p-2 rounded-lg hover:bg-primary/10 text-text-muted hover:text-primary transition-colors"
                                                title="Hızlı Güncelle"
                                            >
                                                <Edit3 size={16} />
                                            </button>
                                            <button
                                                onClick={(e) => { e.stopPropagation(); navigate(`/insaat/projeler/${id}/blok/${blokId}/daire/${daire.id}`); }}
                                                className="p-2 rounded-lg hover:bg-primary/10 text-text-muted hover:text-primary transition-colors"
                                                title="Detay"
                                            >
                                                <Eye size={16} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* Mobil Kart Görünümü */}
                <div className="md:hidden divide-y divide-border-light">
                    {filteredDaireler.map(daire => (
                        <div
                            key={daire.id}
                            className="p-4 hover:bg-bg-hover cursor-pointer transition-colors"
                            onClick={() => navigate(`/insaat/projeler/${id}/blok/${blokId}/daire/${daire.id}`)}
                        >
                            <div className="flex items-center justify-between mb-2">
                                <div>
                                    <span className="font-semibold text-text-primary">{daire.no}</span>
                                    <span className="text-xs text-text-muted ml-2">{daire.kat}. Kat • {daire.tip}</span>
                                </div>
                                {getDurumBadge(daire.durum)}
                            </div>
                            <div className="flex items-center gap-3">
                                <div className="flex-1 h-2 bg-bg-tertiary rounded-full overflow-hidden">
                                    <div className={clsx('h-full rounded-full', getProgressColor(daire.genelIlerleme))} style={{ width: `${daire.genelIlerleme}%` }} />
                                </div>
                                <span className="text-sm font-bold text-text-primary">%{daire.genelIlerleme}</span>
                            </div>
                            {daire.kritikKalem && (
                                <p className="text-xs text-text-muted mt-1">{daire.kritikKalem}</p>
                            )}
                            <div className="flex justify-end mt-2 gap-2">
                                <button
                                    onClick={(e) => { e.stopPropagation(); setQuickEditDaire(daire); }}
                                    className="text-xs text-primary font-medium"
                                >
                                    Hızlı Güncelle
                                </button>
                            </div>
                        </div>
                    ))}
                </div>

                {filteredDaireler.length === 0 && (
                    <div className="text-center py-12">
                        <p className="text-text-muted">Aramanıza uygun daire bulunamadı.</p>
                    </div>
                )}
            </Card>

            {/* Hızlı Güncelleme Modal */}
            {quickEditDaire && (
                <HizliGuncellemeModal
                    isOpen={!!quickEditDaire}
                    onClose={() => setQuickEditDaire(null)}
                    daire={quickEditDaire}
                    blokAdi={blok.name}
                />
            )}
        </div>
    );
}
